import React, { CSSProperties, FunctionComponent, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from '../cn';
import { cssVars as palette } from '../../theme/cssVars';

type TooltipType = 'primary' | 'secondary' | 'error';
type Placement =
  | 'bottom-end' | 'bottom-start' | 'bottom'
  | 'left-end' | 'left-start' | 'left'
  | 'right-end' | 'right-start' | 'right'
  | 'top-end' | 'top-start' | 'top';

type TooltipProps = {
  title: ReactNode,
  values?: { name: string; text: string }[],
  type?: TooltipType,
  placement?: Placement,
  /**
   * Suppress the tip while still rendering the wrapper. Lets a caller turn a
   * tooltip on and off without changing element identity - conditionally
   * wrapping instead would unmount and remount the child, which in a list is
   * a visible hitch.
   */
  disabled?: boolean,
  children: ReactNode,
};

// Same API as the MUI-wrapper Tooltip (title/values/type/placement). Hand-built:
// hover/focus trigger, 100ms enter delay, portaled to <body>, positioned from the
// trigger rect + placement. `values` renders the title + name/text list variant.
// The surface (background, radius, padding, font-size, shadow, max-width) comes
// from `.ds-tooltip`; `type` only recolours it.
const typeStyle: Record<TooltipType, CSSProperties> = {
  primary: { backgroundColor: palette.primary, color: palette.backgroundPaper, opacity: 0.95 },
  secondary: { backgroundColor: palette.secondary, color: palette.content, opacity: 0.95 },
  error: { backgroundColor: palette.error, color: palette.backgroundPaper, opacity: 0.95 },
};

const GAP = 8;

const TooltipUI: FunctionComponent<TooltipProps> = ({ title, values, type, placement = 'right-start', disabled = false, children }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const show = () => { if (disabled) return; timer.current = setTimeout(() => setOpen(true), 100); };
  const hide = () => { clearTimeout(timer.current); setOpen(false); };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tipRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = tipRef.current;
    const side = placement.split('-')[0];
    const align = placement.split('-')[1]; // 'start' | 'end' | undefined (center)

    let top = 0;
    let left = 0;
    if (side === 'top' || side === 'bottom') {
      top = side === 'top' ? t.top - h - GAP : t.bottom + GAP;
      left = align === 'start' ? t.left : align === 'end' ? t.right - w : t.left + t.width / 2 - w / 2;
    } else {
      left = side === 'left' ? t.left - w - GAP : t.right + GAP;
      top = align === 'start' ? t.top : align === 'end' ? t.bottom - h : t.top + t.height / 2 - h / 2;
    }
    setCoords({ top, left });
  }, [open, placement, title, values]);

  // Only the computed position stays inline — it is derived from a live rect.
  const tipStyle: CSSProperties = {
    position: 'fixed',
    top: coords.top,
    left: coords.left,
    zIndex: 1500,
    ...(type ? typeStyle[type] : null),
  };

  return (
    <>
      <div ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        {children}
      </div>
      {open && !disabled && createPortal(
        <div ref={tipRef} role="tooltip" className={cn('ds-tooltip', values && 'px-3 py-2')} style={tipStyle}>
          {values && values.length > 0 ? (
            <div>
              <div className="mb-2 text-sm font-extrabold">{title}</div>
              {values.map((v, i) => (
                <div key={i} className="py-0.5 text-xs">
                  <span className="font-extrabold">{v.name}: </span>
                  <span>{v.text}</span>
                </div>
              ))}
            </div>
          ) : title}
        </div>,
        // Portal into #root so Tailwind's `#root`-scoped utilities apply.
        document.getElementById('root') ?? document.body,
      )}
    </>
  );
};

export default TooltipUI;
